import { useState, useEffect, useRef, useMemo, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, GripVertical, Info, Trash2 } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import AsyncItemSelect from '../components/AsyncItemSelect';
import { createInventoryAdjustment } from '../utils/inventoryAdjustmentApi';
import { getAllAccounts } from '../utils/accountApi';
import { ApiValidationError } from '../types/auth';
import type { Item } from '../types/item';
import type { GlAccount } from '../types/account';
import { formatQuantity } from '../utils/formatCurrency';
import { btnPrimary } from '../utils/uiTheme';
import { toast, toastApiError } from '../utils/toast';
import {
  type AdjustmentLocalLine,
  type AdjustmentPurpose,
  ADJUSTMENT_PURPOSE_OPTIONS,
  canCreateAdjustment,
  changeInQtyForLine,
  defaultAdjustmentAccountId,
  emptyAdjustmentLine,
  filterAdjustmentAccountsByPurpose,
  formatAdjustmentAccountOption,
  newQtyForItem,
  qtyOnHandForLine,
  todayIso,
} from '../utils/inventoryAdjustmentForm';

const InventoryAdjustmentManage = () => {
  const navigate = useNavigate();
  const itemLookupRef = useRef<Map<number, Item>>(new Map());

  const [accounts, setAccounts] = useState<GlAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[] | undefined>>({});

  const [adjustmentDate, setAdjustmentDate] = useState(todayIso);
  const [adjustmentPurpose, setAdjustmentPurpose] = useState<AdjustmentPurpose>('balance_sheet');
  const [adjustmentAccountId, setAdjustmentAccountId] = useState('');
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<AdjustmentLocalLine[]>([emptyAdjustmentLine()]);

  const adjustmentAccounts = useMemo(
    () => filterAdjustmentAccountsByPurpose(accounts, adjustmentPurpose),
    [accounts, adjustmentPurpose]
  );

  useEffect(() => {
    if (!canCreateAdjustment()) {
      navigate('/inventory-adjustments', { replace: true });
      return;
    }

    getAllAccounts()
      .then((accountList) => {
        setAccounts(accountList);
        setAdjustmentAccountId(defaultAdjustmentAccountId(accountList, 'balance_sheet'));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate]);

  const updateLine = (index: number, patch: Partial<AdjustmentLocalLine>) => {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const addLine = () => {
    setLines((prev) => [...prev, emptyAdjustmentLine()]);
  };

  const removeLine = (index: number) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const clearAllLines = () => {
    setLines([emptyAdjustmentLine()]);
  };

  const selectItemForLine = (index: number, item: Item | null) => {
    if (!item) {
      updateLine(index, {
        item_id: 0,
        item_name: '',
        item_sku: '',
        item_description: '',
        qty_on_hand: '0',
        newQty: '',
      });
      return;
    }

    if (item.item_type !== 'INVENTORY' || !item.active) {
      return;
    }

    itemLookupRef.current.set(item.id, item);
    updateLine(index, {
      item_id: item.id,
      item_name: item.name,
      item_sku: item.sku,
      item_description: item.description?.trim() ?? item.category?.fully_qualified_name ?? '',
      qty_on_hand: item.qty_on_hand,
      newQty: newQtyForItem(item),
    });
  };

  const lineFieldError = (index: number, field: string) =>
    fieldErrors[`lines.${index}.${field}`]?.[0] ||
    fieldErrors[`lines.${index}`]?.[0];

  const handlePurposeChange = (purpose: AdjustmentPurpose) => {
    setAdjustmentPurpose(purpose);
    const filtered = filterAdjustmentAccountsByPurpose(accounts, purpose);
    const currentValid = filtered.some((account) => String(account.id) === adjustmentAccountId);
    if (!currentValid) {
      setAdjustmentAccountId(defaultAdjustmentAccountId(accounts, purpose));
    }
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!canCreateAdjustment()) return;

    setFormError(null);
    setFieldErrors({});

    if (!adjustmentAccountId) {
      setFormError('Select an offset account.');
      return;
    }

    const apiLines = lines
      .map((line) => {
        const change = changeInQtyForLine(line);
        const newQty = Number(line.newQty);
        if (!line.item_id || change == null || change === 0 || !Number.isFinite(newQty) || newQty < 0) {
          return null;
        }
        return { item_id: line.item_id, new_qty: newQty };
      })
      .filter(Boolean) as { item_id: number; new_qty: number }[];

    if (apiLines.length === 0) {
      setFormError('Add at least one line where new qty differs from qty on hand.');
      return;
    }

    try {
      setSubmitting(true);
      const created = await createInventoryAdjustment({
        adjustment_date: adjustmentDate,
        adjustment_account_id: Number(adjustmentAccountId),
        memo: memo.trim() || null,
        lines: apiLines,
      });
      toast.success(`${created.adjustment_number} created.`);
      navigate(`/inventory-adjustments/${created.id}`);
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFormError(err.message);
        setFieldErrors(err.errors);
      } else {
        toastApiError(err, 'Failed to create adjustment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-800" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <form onSubmit={handleCreate} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-auto p-6 pb-28">
            <div className="mb-6">
              <Link
                to="/inventory-adjustments"
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3"
              >
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                Back to Inventory Adjustments
              </Link>
              <h1 className="text-2xl font-semibold text-gray-900">New Inventory Adjustment</h1>
              <p className="text-sm text-gray-600 mt-1">
                Correct counts without P&amp;L impact, or record shrinkage — inventory and GL post on
                save
              </p>
            </div>

            {formError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-400 shrink-0" />
                  <p className="text-red-800 text-sm">{formError}</p>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8 space-y-8">
              <section className="pb-8 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900 mb-5">Adjustment details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Adjustment date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={adjustmentDate}
                      onChange={(e) => setAdjustmentDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                    />
                    {fieldErrors.adjustment_date && (
                      <p className="text-xs text-red-600 mt-1">{fieldErrors.adjustment_date[0]}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Reference no.
                    </label>
                    <input
                      type="text"
                      value={memo}
                      onChange={(e) => setMemo(e.target.value)}
                      placeholder="Optional"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                    />
                  </div>
                  <div className="sm:col-span-2 xl:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Adjustment purpose
                    </label>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {ADJUSTMENT_PURPOSE_OPTIONS.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => handlePurposeChange(option.id)}
                          className={`px-3 py-1.5 text-sm rounded-lg border ${
                            adjustmentPurpose === option.id
                              ? 'bg-slate-800 text-white border-slate-800'
                              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <label className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 mb-1.5">
                      Offset account <span className="text-red-500">*</span>
                      <span title="Balance sheet accounts (equity or variance asset) avoid P&L. Expense accounts record shrinkage or write-offs.">
                        <Info className="h-3.5 w-3.5 text-gray-400" />
                      </span>
                    </label>
                    <select
                      required
                      value={adjustmentAccountId}
                      onChange={(e) => setAdjustmentAccountId(e.target.value)}
                      className="w-full max-w-xl px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                    >
                      <option value="">Select account…</option>
                      {adjustmentAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {formatAdjustmentAccountOption(account)}
                        </option>
                      ))}
                    </select>
                    {adjustmentAccounts.length === 0 && (
                      <p className="text-xs text-amber-700 mt-1">
                        {adjustmentPurpose === 'balance_sheet'
                          ? 'No equity or variance asset accounts available — create one in Chart of Accounts.'
                          : 'No expense accounts available — create one in Chart of Accounts.'}
                      </p>
                    )}
                    {fieldErrors.adjustment_account_id && (
                      <p className="text-xs text-red-600 mt-1">
                        {fieldErrors.adjustment_account_id[0]}
                      </p>
                    )}
                  </div>
                </div>
              </section>

              <section>
                <h3 className="text-base font-semibold text-gray-900 mb-5">Line items</h3>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full min-w-[960px] text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="w-8 px-2 py-2" aria-hidden="true" />
                        <th className="w-10 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          #
                        </th>
                        <th className="min-w-[180px] px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Product/Service
                        </th>
                        <th className="w-24 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          SKU
                        </th>
                        <th className="min-w-[140px] px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Description
                        </th>
                        <th className="w-28 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Qty on hand
                        </th>
                        <th className="w-28 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          New qty
                        </th>
                        <th className="w-32 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Change in qty
                        </th>
                        <th className="w-10 px-2 py-2" aria-hidden="true" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {lines.map((line, index) => {
                        const qtyOnHand = qtyOnHandForLine(line);
                        const change = changeInQtyForLine(line);
                        const lineError = lineFieldError(index, 'new_qty');

                        return (
                          <tr key={index} className="hover:bg-gray-50/80 align-top">
                            <td className="px-2 py-2 text-gray-300">
                              <GripVertical className="h-4 w-4" />
                            </td>
                            <td className="px-2 py-2 text-gray-500">{index + 1}</td>
                            <td className="px-2 py-2">
                              <AsyncItemSelect
                                className="w-full"
                                value={line.item_id}
                                selectedLabel={line.item_name}
                                onSelect={(item) => selectItemForLine(index, item)}
                                onClear={() => selectItemForLine(index, null)}
                                placeholder="Select product"
                                searchPlaceholder="Search by name or SKU..."
                                itemType="INVENTORY"
                              />
                            </td>
                            <td className="px-2 py-2 text-gray-600 font-mono text-xs">
                              {line.item_sku || '—'}
                            </td>
                            <td className="px-2 py-2 text-gray-600 text-xs truncate max-w-[180px]">
                              {line.item_description || '—'}
                            </td>
                            <td className="px-2 py-2 text-right font-mono text-gray-700">
                              {formatQuantity(qtyOnHand)}
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="number"
                                step="any"
                                min="0"
                                value={line.newQty}
                                onChange={(e) => updateLine(index, { newQty: e.target.value })}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right font-mono focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                              />
                              {lineError && (
                                <p className="text-xs text-red-600 mt-1 text-left">{lineError}</p>
                              )}
                            </td>
                            <td
                              className={`px-2 py-2 text-right font-mono ${
                                change == null || change === 0
                                  ? 'text-gray-400'
                                  : change > 0
                                    ? 'text-emerald-600'
                                    : 'text-red-600'
                              }`}
                            >
                              {change == null
                                ? '—'
                                : `${change > 0 ? '+' : ''}${formatQuantity(change)}`}
                            </td>
                            <td className="px-2 py-2 text-center">
                              <button
                                type="button"
                                onClick={() => removeLine(index)}
                                disabled={lines.length <= 1}
                                className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
                                title="Remove line"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {fieldErrors.lines && (
                  <p className="text-xs text-red-600 mt-2">{fieldErrors.lines[0]}</p>
                )}

                <div className="flex flex-wrap gap-3 mt-4">
                  <button
                    type="button"
                    onClick={addLine}
                    className="px-4 py-1.5 text-sm font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50"
                  >
                    Add lines
                  </button>
                  <button
                    type="button"
                    onClick={clearAllLines}
                    className="px-4 py-1.5 text-sm font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50"
                  >
                    Clear all lines
                  </button>
                </div>
              </section>
            </div>
          </div>

          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
            <div className="flex items-center justify-end gap-3 flex-wrap">
              <Link
                to="/inventory-adjustments"
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={submitting}
                className={`${btnPrimary} px-6 py-2.5 text-sm font-semibold disabled:opacity-50`}
              >
                {submitting ? 'Saving…' : 'Save adjustment'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InventoryAdjustmentManage;
